import type { AnalyticsAdapter, AnalyticsProps, TrackOptions } from "./AnalyticsAdapter";

type PendingCall = (adapter: AnalyticsAdapter) => void;

/** Upper bound on calls held while an SDK is still loading. */
export const DEFERRED_BUFFER_LIMIT = 200;

/**
 * Wraps a destination whose SDK loads asynchronously (dynamic import in
 * `init()`). Every call made before `init` settles is buffered and replayed in
 * the original order once the SDK is ready; later calls pass straight through.
 * Without this, the startup burst (`identify`, person props, `app_open`,
 * `session_started`) hits a destination whose client is still `null` and is
 * silently dropped. A rejected `init` still triggers the replay: the wrapped
 * destination no-ops on its own when it has no client. `optOut` discards the
 * buffer so nothing captured before a consent denial is ever sent.
 */
export class DeferredAnalyticsAdapter implements AnalyticsAdapter {
  private readonly inner: AnalyticsAdapter;
  private pending: PendingCall[] | null = [];

  constructor(inner: AnalyticsAdapter, ready: Promise<void>) {
    this.inner = inner;
    ready.catch(() => undefined).then(() => this.replay());
  }

  private replay(): void {
    const queued = this.pending ?? [];
    this.pending = null;
    for (const call of queued) {
      call(this.inner);
    }
  }

  private enqueue(call: PendingCall): void {
    if (!this.pending) {
      call(this.inner);
      return;
    }
    const kept = this.pending.length >= DEFERRED_BUFFER_LIMIT ? this.pending.slice(1) : this.pending;
    this.pending = [...kept, call];
  }

  track(event: string, props?: AnalyticsProps, options?: TrackOptions): void {
    this.enqueue((adapter) =>
      options ? adapter.track(event, props, options) : adapter.track(event, props),
    );
  }

  identify(anonId: string): void {
    this.enqueue((adapter) => adapter.identify(anonId));
  }

  setUserProps(props: AnalyticsProps): void {
    this.enqueue((adapter) => adapter.setUserProps(props));
  }

  flush(): void {
    this.enqueue((adapter) => adapter.flush());
  }

  optOut(): void {
    if (this.pending) {
      this.pending = [];
    }
    this.enqueue((adapter) => adapter.optOut());
  }

  reset(): void {
    this.enqueue((adapter) => adapter.reset());
  }
}
